import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, XCircle, Plus, Trash2, Save, Users, Wifi, Clock, WifiOff, Loader2 } from 'lucide-react';
import { todayMx, shiftDateStr } from '../lib/dateUtils';

const TZ = 'America/Tijuana';
const formatMxTime = (d) => new Intl.DateTimeFormat('es-MX', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true }).format(d).toUpperCase();
const formatMxDate = (d) => new Intl.DateTimeFormat('es-MX', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' }).format(d);

const TIPO_LABELS = {
  admin: { label: 'Admin', cls: 'bg-purple-100 text-purple-800' },
  oficina: { label: 'Oficina', cls: 'bg-blue-100 text-blue-800' },
  tecnico: { label: 'Técnico', cls: 'bg-amber-100 text-amber-800' },
  ayudante: { label: 'Ayudante', cls: 'bg-emerald-100 text-emerald-800' },
};

export default function AttendanceAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMode, setFilterMode] = useState('day');
  const [filterDate, setFilterDate] = useState(todayMx());
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [cfg, setCfg] = useState({ allowed_ips: [] });
  const [newIp, setNewIp] = useState('');
  const [myIp, setMyIp] = useState('');
  const [savingCfg, setSavingCfg] = useState(false);
  const [myStatus, setMyStatus] = useState(null); // {connected, already_registered, record, client_ip}
  const [checkingIn, setCheckingIn] = useState(false);
  const [nowDate, setNowDate] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNowDate(new Date()), 1000); return () => clearInterval(t); }, []);

  const rangeParams = useMemo(() => {
    if (filterMode === 'day') return { date_from: filterDate, date_to: filterDate };
    if (filterMode === 'week') return { date_from: shiftDateStr(filterDate, -6), date_to: filterDate };
    if (filterMode === 'month') return { date_from: shiftDateStr(filterDate, -29), date_to: filterDate };
    if (filterMode === 'range') return { date_from: filterFrom || undefined, date_to: filterTo || undefined };
    return {};
  }, [filterMode, filterDate, filterFrom, filterTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...rangeParams };
      if (filterTipo !== 'all') params.tipo = filterTipo;
      if (filterUser !== 'all') params.user_id = filterUser;
      const cfgPromise = isAdmin ? axios.get(`${API}/attendance/config`).catch(() => ({ data: { allowed_ips: [] } })) : Promise.resolve({ data: { allowed_ips: [] } });
      const [rRec, rEmp, rCfg, rSt] = await Promise.all([
        axios.get(`${API}/attendance/list`, { params }),
        axios.get(`${API}/attendance/employees`),
        cfgPromise,
        axios.get(`${API}/attendance/status`),
      ]);
      setRecords(rRec.data || []);
      setEmployees(rEmp.data || []);
      setCfg(rCfg.data || { allowed_ips: [] });
      setMyIp(rSt.data?.client_ip || '');
      setMyStatus(rSt.data || null);
    } catch (err) {
      toast.error('Error al cargar asistencias');
    } finally { setLoading(false); }
  }, [rangeParams, filterTipo, filterUser, isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Mapa user_id -> record del rango
  const recordByUser = useMemo(() => {
    const m = {};
    // Si es rango de 1 día, mostrar directo; si es rango, agrupar por user pero mostrar el más reciente
    for (const r of records) {
      if (!m[r.user_id] || r.timestamp_utc > m[r.user_id].timestamp_utc) m[r.user_id] = r;
    }
    return m;
  }, [records]);

  const employeesFiltered = useMemo(() => {
    let list = employees;
    if (filterTipo !== 'all') list = list.filter(e => e.tipo === filterTipo);
    if (filterUser !== 'all') list = list.filter(e => e.id === filterUser);
    return list;
  }, [employees, filterTipo, filterUser]);

  const shiftDay = (delta) => setFilterDate(shiftDateStr(filterDate, delta));

  const addIp = () => {
    const ip = newIp.trim();
    if (!ip) return;
    if (cfg.allowed_ips.includes(ip)) { toast.error('IP ya está en la lista'); return; }
    setCfg({ ...cfg, allowed_ips: [...cfg.allowed_ips, ip] });
    setNewIp('');
  };
  const removeIp = (ip) => setCfg({ ...cfg, allowed_ips: cfg.allowed_ips.filter(x => x !== ip) });
  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await axios.put(`${API}/attendance/config`, { allowed_ips: cfg.allowed_ips });
      toast.success('IPs guardadas');
    } catch { toast.error('Error al guardar'); }
    finally { setSavingCfg(false); }
  };
  const addCurrentIp = () => {
    if (!myIp) { toast.error('No se detectó tu IP'); return; }
    if (cfg.allowed_ips.includes(myIp)) { toast.info('Tu IP ya está autorizada'); return; }
    setCfg({ ...cfg, allowed_ips: [...cfg.allowed_ips, myIp] });
    toast.success(`IP ${myIp} agregada (no olvides guardar)`);
  };

  const doCheckin = async () => {
    setCheckingIn(true);
    try {
      const r = await axios.post(`${API}/attendance/checkin`);
      toast.success('¡Llegada registrada!');
      setMyStatus(prev => ({ ...(prev || {}), already_registered: true, record: r.data.record }));
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo registrar');
    } finally { setCheckingIn(false); }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="attendance-admin-page">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6 text-blue-600" />Asistencias</h2>
        <p className="text-muted-foreground text-sm">Registra tu llegada y revisa quién llegó a la oficina.</p>
      </div>

      {/* MI ASISTENCIA — bloque personal para admin/oficina */}
      <Card className="border-2 border-blue-200 dark:border-blue-900" data-testid="my-attendance-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" />Mi asistencia de hoy</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-[1fr_auto] gap-3 items-center">
          <div>
            <p className="text-sm">Hola, <strong>{(user?.full_name || '').split(' ')[0]}</strong></p>
            <p className="text-xs text-muted-foreground capitalize">{formatMxDate(nowDate)}</p>
            <p className="text-2xl font-bold tabular-nums mt-1">{formatMxTime(nowDate)}</p>
            {myStatus?.ip_bypass ? (
              <p className="text-xs text-blue-700 flex items-center gap-1 mt-1"><Wifi className="h-3 w-3" />Puedes registrar tu llegada desde cualquier red</p>
            ) : myStatus?.connected ? (
              <p className="text-xs text-emerald-700 flex items-center gap-1 mt-1"><Wifi className="h-3 w-3" />Internet de oficina detectado</p>
            ) : (
              <p className="text-xs text-orange-700 flex items-center gap-1 mt-1"><WifiOff className="h-3 w-3" />No estás en la red autorizada de la oficina</p>
            )}
          </div>
          <div>
            {myStatus?.already_registered && myStatus?.record ? (
              <div className="text-right sm:text-center rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 p-3 min-w-[180px]" data-testid="my-attendance-done">
                <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
                <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mt-1">Asistencia registrada</p>
                <p className="text-lg font-bold tabular-nums text-emerald-800 dark:text-emerald-200">{myStatus.record.hora_llegada}</p>
              </div>
            ) : (
              <Button
                onClick={doCheckin}
                disabled={!myStatus?.connected || checkingIn}
                className="h-14 w-full sm:w-auto px-6 text-base font-bold"
                data-testid="my-attendance-checkin-btn"
              >
                {checkingIn ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />Registrando...</> : <><Clock className="h-5 w-5 mr-2" />Registrar mi llegada</>}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Wifi className="h-4 w-4" />IPs autorizadas para asistencia</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="text-xs text-muted-foreground">Solo desde estas IP públicas los trabajadores podrán registrar su llegada. Tu IP actual: <strong>{myIp || '—'}</strong></div>
            <div className="flex flex-wrap gap-2">
              {cfg.allowed_ips.length === 0 && <span className="text-xs text-orange-700">Aún no hay IPs configuradas. Nadie puede registrar hasta que agregues al menos una.</span>}
              {cfg.allowed_ips.map(ip => (
                <span key={ip} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs">
                  {ip}
                  <button onClick={() => removeIp(ip)} className="hover:text-red-600" data-testid={`ip-remove-${ip}`}><Trash2 className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1"><Label className="text-xs">Nueva IP</Label><Input value={newIp} onChange={e => setNewIp(e.target.value)} placeholder="200.123.45.67" data-testid="ip-input" /></div>
              <Button type="button" variant="outline" size="sm" onClick={addIp} data-testid="ip-add-btn"><Plus className="h-4 w-4 mr-1" />Agregar</Button>
              <Button type="button" variant="outline" size="sm" onClick={addCurrentIp} data-testid="ip-add-current"><Wifi className="h-4 w-4 mr-1" />Usar mi IP ({myIp})</Button>
              <Button type="button" size="sm" onClick={saveCfg} disabled={savingCfg} data-testid="ip-save-btn"><Save className="h-4 w-4 mr-1" />{savingCfg ? 'Guardando...' : 'Guardar'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-3 flex flex-wrap gap-2 items-end">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => shiftDay(-1)}>◀</Button>
            <Input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="w-[150px]" data-testid="att-date" />
            <Button variant="outline" size="sm" onClick={() => shiftDay(1)}>▶</Button>
          </div>
          <div className="flex gap-1">
            <Button variant={filterMode === 'day' ? 'default' : 'outline'} size="sm" onClick={() => { setFilterMode('day'); setFilterDate(todayMx()); }}>Hoy</Button>
            <Button variant={filterMode === 'week' ? 'default' : 'outline'} size="sm" onClick={() => setFilterMode('week')}>7 días</Button>
            <Button variant={filterMode === 'month' ? 'default' : 'outline'} size="sm" onClick={() => setFilterMode('month')}>30 días</Button>
          </div>
          <div className="space-y-1"><Label className="text-xs">Tipo</Label>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-[130px]" data-testid="att-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="tecnico">Técnicos</SelectItem>
                <SelectItem value="ayudante">Ayudantes</SelectItem>
                <SelectItem value="oficina">Oficina</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Empleado</Label>
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-center text-muted-foreground py-8">Cargando...</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filterMode === 'day' ? (
            // Mostrar todos los empleados y su estado del día
            employeesFiltered.map(emp => {
              const rec = recordByUser[emp.id];
              const tl = TIPO_LABELS[emp.tipo] || TIPO_LABELS.oficina;
              return (
                <Card key={emp.id} className={rec ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-slate-300 opacity-80'} data-testid={`att-card-${emp.id}`}>
                  <CardContent className="p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{emp.full_name}</p>
                      <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${tl.cls}`}>{tl.label}</span>
                    </div>
                    <div className="text-right">
                      {rec ? (
                        <>
                          <div className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-semibold">Llegó</span></div>
                          <div className="text-sm tabular-nums">{rec.hora_llegada}</div>
                        </>
                      ) : (
                        <div className="flex items-center gap-1 text-slate-500"><XCircle className="h-4 w-4" /><span className="text-sm">Sin registrar</span></div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            // Rango — mostrar lista de registros
            records.length === 0 ? <p className="text-center text-muted-foreground py-8 col-span-full">Sin registros en el rango</p> :
            records.map(r => {
              const tl = TIPO_LABELS[r.tipo] || TIPO_LABELS.oficina;
              return (
                <Card key={r.id} className="border-l-4 border-l-emerald-500">
                  <CardContent className="p-3">
                    <p className="font-semibold">{r.user_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${tl.cls}`}>{tl.label}</span>
                      <Badge variant="outline" className="text-[10px]">{r.fecha}</Badge>
                    </div>
                    <p className="text-lg font-bold mt-1 tabular-nums">{r.hora_llegada}</p>
                    <p className="text-[10px] text-muted-foreground">IP: {r.ip_usada}</p>
                  </CardContent>
                </Card>
              );
            })
          )}
          {filterMode === 'day' && employeesFiltered.length === 0 && (
            <p className="text-center text-muted-foreground py-8 col-span-full">Sin empleados en el filtro</p>
          )}
        </div>
      )}
    </div>
  );
}
